import { ScoreResponse } from "@/pages/Private/pages/Score/schema/score.schema";

import {
	KnowledgeDbIcpApi,
	KnowledgeDbProductApi,
	KnowledgeDbCaseStudyApi,
	KnowledgeDbObjectionApi,
	KnowledgeDbObjectionCategoryApi,
	KnowledgeDbSettingApi,
} from "../schema/knowledgeDbApi";
import {
	ALL_SECTION_NAMES,
	CompanySegment,
	ContentStatus,
	FeatureBenefitRow,
	KnowledgeDbTab,
	PainPoint,
	SectionContentStatuses,
	SectionName,
	SegmentContent,
	TabStatus,
} from "../schema/knowledgeDb";
import { Product, ProductType } from "../schema/productsServices";
import { CaseStudy, AutoUpdateSettings } from "../schema/caseStudies";
import { Objection, ObjectionCategory, HandlingStrategy } from "../schema/objectionHandling";

// ── Status Mapping Helper ──

const STATUS_MAP: Record<string, ContentStatus> = {
	AI_DRAFTED: ContentStatus.AI_DRAFTED,
	ai_drafted: ContentStatus.AI_DRAFTED,
	HUMAN_APPROVED: ContentStatus.HUMAN_APPROVED,
	human_approved: ContentStatus.HUMAN_APPROVED,
	MANUAL: ContentStatus.MANUAL,
	manual: ContentStatus.MANUAL,
	DRAFT: ContentStatus.DRAFT,
	draft: ContentStatus.DRAFT,
};

export function mapStringToContentStatus(value: string | null | undefined): ContentStatus {
	if (!value) {
		return ContentStatus.AI_DRAFTED;
	}

	return STATUS_MAP[value] ?? ContentStatus.AI_DRAFTED;
}

/**
 * Normalize a raw contentStatuses object from the API.
 * Converts each value through mapStringToContentStatus so
 * both lowercase and uppercase values map correctly to the enum.
 */
function normalizeContentStatuses(
	raw: Record<string, string> | null | undefined
): SectionContentStatuses {
	if (!raw || typeof raw !== "object") {
		return {};
	}

	const result: SectionContentStatuses = {};

	for (const section of ALL_SECTION_NAMES) {
		const value = raw[section];

		if (value !== undefined && value !== null) {
			result[section] = mapStringToContentStatus(value);
		}
	}

	return result;
}

// ── JSON Wrapper Helpers ──

export function fromApiJsonWrapper<T>(obj: Record<string, unknown> | null | undefined): T[] {
	if (!obj || typeof obj !== "object") {
		return [];
	}

	const items = (obj as { items?: unknown }).items;

	if (!Array.isArray(items)) {
		return [];
	}

	return items as T[];
}

export function toApiJsonWrapper<T>(items: T[]): { items: T[] } {
	return { items };
}

// ── Segment Tree Builder ──

export function buildSegmentTree(score: ScoreResponse): CompanySegment[] {
	const companyCategories = score.prospectCompanyCategories ?? [];
	const personaCategories = score.prospectPersonaCategories ?? [];

	return companyCategories.map((cc) => ({
		id: cc.id ?? 0,
		name: cc.category,
		personas: personaCategories
			.filter((pc) => pc.scoreCompanyCategories.some((scc) => scc.id === cc.id))
			.map((pc) => ({
				id: pc.id ?? 0,
				name: pc.category,
			})),
	}));
}

// ── ICP Transformers ──

export function apiIcpToSegmentContent(apiIcp: KnowledgeDbIcpApi): SegmentContent {
	return {
		id: apiIcp.id,
		scorePersonaCategoryId: apiIcp.scorePersonaCategoryId,
		scoreCompanyCategoryId: apiIcp.scoreCompanyCategoryId ?? null,
		profile: apiIcp.profile ?? "",
		painPoints: fromApiJsonWrapper<PainPoint>(apiIcp.painPoints as Record<string, unknown>),
		currentSolutions: apiIcp.currentSolutions ?? "",
		marketTrends: apiIcp.marketTrends ?? "",
		obstacles: fromApiJsonWrapper<string>(apiIcp.obstacles as Record<string, unknown>),
		opportunities: fromApiJsonWrapper<string>(apiIcp.opportunities as Record<string, unknown>),
		featuresBenefits: fromApiJsonWrapper<FeatureBenefitRow>(
			apiIcp.featuresBenefits as Record<string, unknown>
		),
		contentStatuses: normalizeContentStatuses(
			(apiIcp.contentStatuses as Record<string, string>) ?? {}
		),
		companyId: apiIcp.companyId,
		createdAt: apiIcp.createdAt,
		updatedAt: apiIcp.updatedAt,
	};
}

/**
 * Collapse the ICP list to one content per company×persona segment, keeping the first
 * occurrence — the same row `SegmentSidebar` resolves with `.find()`.
 *
 * The API can return several ICPs for one segment (repeated AI generation appended copies
 * instead of replacing them). The sidebar has always shown exactly one of them, so approving
 * a segment only ever touched that row while the hidden copies stayed AI_DRAFTED and kept the
 * tab badge lit (PREC-3700). Deduping at the source keeps the sidebar and the badge counting
 * the same rows even if duplicates reappear.
 */
export function dedupeContentsBySegment(contents: SegmentContent[]): SegmentContent[] {
	const seen = new Set<string>();

	return contents.filter((content) => {
		const key = `${content.scoreCompanyCategoryId}:${content.scorePersonaCategoryId}`;

		if (seen.has(key)) {
			return false;
		}

		seen.add(key);

		return true;
	});
}

export function segmentContentToApiPayload(content: Partial<SegmentContent>) {
	return {
		...(content.profile !== undefined && { profile: content.profile }),
		...(content.painPoints !== undefined && { painPoints: toApiJsonWrapper(content.painPoints) }),
		...(content.currentSolutions !== undefined && { currentSolutions: content.currentSolutions }),
		...(content.marketTrends !== undefined && { marketTrends: content.marketTrends }),
		...(content.obstacles !== undefined && { obstacles: toApiJsonWrapper(content.obstacles) }),
		...(content.opportunities !== undefined && {
			opportunities: toApiJsonWrapper(content.opportunities),
		}),
		...(content.featuresBenefits !== undefined && {
			featuresBenefits: toApiJsonWrapper(content.featuresBenefits),
		}),
		...(content.scorePersonaCategoryId !== undefined && {
			scorePersonaCategoryId: content.scorePersonaCategoryId,
		}),
		...(content.scoreCompanyCategoryId !== undefined && {
			scoreCompanyCategoryId: content.scoreCompanyCategoryId,
		}),
		...(content.contentStatuses !== undefined && {
			contentStatuses: content.contentStatuses,
		}),
	};
}

// ── Product Transformers ──

export function apiProductToProduct(apiProduct: KnowledgeDbProductApi): Product {
	return {
		id: apiProduct.id,
		name: apiProduct.name ?? "",
		type: (apiProduct.type as ProductType) ?? ProductType.PRODUCT,
		description: apiProduct.description ?? "",
		keyFeatures: fromApiJsonWrapper<string>(apiProduct.keyFeatures as Record<string, unknown>),
		targetSegments: (apiProduct.targetSegments ?? []).map((ts) => ({
			id: ts.id,
			scoreCompanyCategoryId: ts.scoreCompanyCategoryId,
			name: ts.scoreCompanyCategory?.category ?? `Segment ${ts.scoreCompanyCategoryId}`,
			isActive: ts.isActive,
		})),
		competitiveDiff: apiProduct.competitiveDiff ?? "",
		pricing: apiProduct.pricing ?? "",
		status: mapStringToContentStatus(apiProduct.status),
		companyId: apiProduct.companyId,
		createdAt: apiProduct.createdAt,
		updatedAt: apiProduct.updatedAt,
	};
}

export function productToApiPayload(product: Partial<Product>) {
	return {
		...(product.name !== undefined && { name: product.name }),
		...(product.type !== undefined && { type: product.type }),
		...(product.description !== undefined && { description: product.description }),
		...(product.keyFeatures !== undefined && {
			keyFeatures: toApiJsonWrapper(product.keyFeatures),
		}),
		...(product.competitiveDiff !== undefined && { competitiveDiff: product.competitiveDiff }),
		...(product.pricing !== undefined && { pricing: product.pricing }),
		...(product.status !== undefined && { status: product.status }),
		...(product.targetSegments !== undefined && {
			targetSegments: product.targetSegments.map((ts) => ({
				scoreCompanyCategoryId: ts.scoreCompanyCategoryId,
				isActive: ts.isActive,
			})),
		}),
	};
}

// ── Case Study Transformers ──

export function apiCaseStudyToCaseStudy(apiCs: KnowledgeDbCaseStudyApi): CaseStudy {
	return {
		id: apiCs.id,
		clientName: apiCs.clientName ?? "",
		website: apiCs.website ?? "",
		matchedSegment: apiCs.matchedSegment ?? "",
		description: apiCs.description ?? "",
		industry: apiCs.industry ?? [],
		employeeCount: apiCs.employeeCount ?? 0,
		enrichmentSource: apiCs.enrichmentSource ?? "",
		projectName: apiCs.projectName ?? "",
		goalsChallenge: apiCs.goalsChallenge ?? "",
		solution: apiCs.solution ?? "",
		outcome: apiCs.outcome ?? "",
		quote: apiCs.quote ?? "",
		quoteAttribution: apiCs.quoteAttribution ?? "",
		sourceUrl: apiCs.sourceUrl ?? "",
		detectedDate: apiCs.detectedDate ?? "",
		status: mapStringToContentStatus(apiCs.status),
		companyId: apiCs.companyId,
		createdAt: apiCs.createdAt,
		updatedAt: apiCs.updatedAt,
	};
}

export function caseStudyToApiPayload(cs: Partial<CaseStudy>) {
	return {
		...(cs.clientName !== undefined && { clientName: cs.clientName }),
		...(cs.website !== undefined && { website: cs.website }),
		...(cs.matchedSegment !== undefined && { matchedSegment: cs.matchedSegment }),
		...(cs.description !== undefined && { description: cs.description }),
		...(cs.industry !== undefined && { industry: cs.industry }),
		...(cs.employeeCount !== undefined && { employeeCount: cs.employeeCount }),
		...(cs.enrichmentSource !== undefined && { enrichmentSource: cs.enrichmentSource }),
		...(cs.projectName !== undefined && { projectName: cs.projectName }),
		...(cs.goalsChallenge !== undefined && { goalsChallenge: cs.goalsChallenge }),
		...(cs.solution !== undefined && { solution: cs.solution }),
		...(cs.outcome !== undefined && { outcome: cs.outcome }),
		...(cs.quote !== undefined && { quote: cs.quote }),
		...(cs.quoteAttribution !== undefined && { quoteAttribution: cs.quoteAttribution }),
		...(cs.sourceUrl !== undefined && { sourceUrl: cs.sourceUrl }),
		...(cs.detectedDate !== undefined && { detectedDate: cs.detectedDate }),
		...(cs.status !== undefined && { status: cs.status }),
	};
}

// ── Objection Transformers ──

export function apiObjectionToObjection(apiObj: KnowledgeDbObjectionApi): Objection {
	return {
		id: apiObj.id,
		categoryId: apiObj.categoryId ?? 0,
		quote: apiObj.quote ?? "",
		underlyingConcern: apiObj.underlyingConcern ?? "",
		strategies: fromApiJsonWrapper<HandlingStrategy>(apiObj.strategies as Record<string, unknown>),
		status: mapStringToContentStatus(apiObj.status),
		companyId: apiObj.companyId,
		createdAt: apiObj.createdAt,
		updatedAt: apiObj.updatedAt,
	};
}

export function objectionToApiPayload(obj: Partial<Objection>) {
	return {
		...(obj.quote !== undefined && { quote: obj.quote }),
		...(obj.underlyingConcern !== undefined && { underlyingConcern: obj.underlyingConcern }),
		...(obj.strategies !== undefined && { strategies: toApiJsonWrapper(obj.strategies) }),
		...(obj.status !== undefined && { status: obj.status }),
		...(obj.categoryId !== undefined && { categoryId: obj.categoryId }),
	};
}

// ── Objection Category Transformers ──

export function apiCategoryToCategory(apiCat: KnowledgeDbObjectionCategoryApi): ObjectionCategory {
	return {
		id: apiCat.id,
		name: apiCat.name,
		isCustom: apiCat.isCustom,
	};
}

// ── Settings Transformers ──

export function apiSettingToAutoUpdateSettings(
	apiSetting: KnowledgeDbSettingApi
): AutoUpdateSettings {
	return {
		id: apiSetting.id,
		autoUpdateEnabled: apiSetting.autoUpdateEnabled,
		caseStudyUrl: apiSetting.caseStudyUrl ?? "",
		caseStudyDocumentUrl: apiSetting.caseStudyDocumentUrl ?? "",
		lastAutoUpdate: apiSetting.lastAutoUpdate ?? null,
		autoUpdateInProgress: apiSetting.autoUpdateInProgress,
		userId: apiSetting.userId,
		companyId: apiSetting.companyId,
		createdAt: apiSetting.createdAt,
		updatedAt: apiSetting.updatedAt,
	};
}

// ── Status Helpers ──

/**
 * Compute the overall approval status for a single segment.
 * - If any section is AI_DRAFTED or DRAFT → AI_DRAFTED
 * - If all populated sections are HUMAN_APPROVED → HUMAN_APPROVED
 * - If all populated sections are MANUAL → MANUAL (no badge)
 * - If no statuses at all → null (treat as no data)
 */
export function getSegmentOverallStatus(content: SegmentContent): ContentStatus | null {
	const statuses = ALL_SECTION_NAMES.map((s) => content.contentStatuses[s]).filter(
		(s): s is ContentStatus => s !== undefined
	);

	if (statuses.length === 0) {
		return null;
	}

	if (statuses.some((s) => s === ContentStatus.AI_DRAFTED || s === ContentStatus.DRAFT)) {
		return ContentStatus.AI_DRAFTED;
	}

	if (statuses.every((s) => s === ContentStatus.MANUAL)) {
		return ContentStatus.MANUAL;
	}

	return ContentStatus.HUMAN_APPROVED;
}

/**
 * Build a single section's status, defaulting to AI_DRAFTED for sections that
 * have content but no explicit status (backward compatibility).
 */
export function getSectionStatus(
	content: SegmentContent,
	section: SectionName
): ContentStatus | undefined {
	return content.contentStatuses[section];
}

/**
 * Mark all sections of a segment as HUMAN_APPROVED and return the new statuses map.
 */
export function approveAllSections(content: SegmentContent): SectionContentStatuses {
	const next: SectionContentStatuses = { ...content.contentStatuses };

	for (const section of ALL_SECTION_NAMES) {
		next[section] = ContentStatus.HUMAN_APPROVED;
	}

	return next;
}

/**
 * Mark all sections of a segment as DRAFT and return the new statuses map.
 */
export function unapproveAllSections(content: SegmentContent): SectionContentStatuses {
	const next: SectionContentStatuses = { ...content.contentStatuses };

	for (const section of ALL_SECTION_NAMES) {
		next[section] = ContentStatus.DRAFT;
	}

	return next;
}

/**
 * Compute an overall status from a list of entity-level statuses.
 * - If any is AI_DRAFTED or DRAFT → AI_DRAFTED
 * - If all are MANUAL → MANUAL
 * - Otherwise → HUMAN_APPROVED
 * - Empty list → null
 */
export function getOverallStatusFromList(statuses: ContentStatus[]): ContentStatus | null {
	if (statuses.length === 0) {
		return null;
	}

	if (statuses.some((s) => s === ContentStatus.AI_DRAFTED || s === ContentStatus.DRAFT)) {
		return ContentStatus.AI_DRAFTED;
	}

	if (statuses.every((s) => s === ContentStatus.MANUAL)) {
		return ContentStatus.MANUAL;
	}

	return ContentStatus.HUMAN_APPROVED;
}

/**
 * Compute tab-level overall status.
 * PAIN_POINTS: uses per-section statuses from SegmentContent
 * Other tabs: uses entity-level status field
 */
export function getTabOverallStatus(
	contents: SegmentContent[],
	tab: KnowledgeDbTab,
	entityStatuses?: ContentStatus[]
): ContentStatus | null {
	if (tab === KnowledgeDbTab.PAIN_POINTS) {
		const segmentStatuses = contents
			.map(getSegmentOverallStatus)
			.filter((s): s is ContentStatus => s !== null);

		return getOverallStatusFromList(segmentStatuses);
	}

	// Products, Case Studies, Objections use entity-level statuses
	if (entityStatuses && entityStatuses.length > 0) {
		return getOverallStatusFromList(entityStatuses);
	}

	return null;
}

interface ComputeTabStatusesInput {
	contents: SegmentContent[];
	products: Product[];
	caseStudies: CaseStudy[];
	objections: Objection[];
}

/**
 * Compute tab statuses for all tabs from the current data.
 */
export function computeTabStatuses(input: ComputeTabStatusesInput): TabStatus[] {
	const { contents, products, caseStudies, objections } = input;

	const entries: Array<{ tab: KnowledgeDbTab; entityStatuses?: ContentStatus[] }> = [
		{ tab: KnowledgeDbTab.PAIN_POINTS },
		{ tab: KnowledgeDbTab.PRODUCTS_SERVICES, entityStatuses: products.map((p) => p.status) },
		{ tab: KnowledgeDbTab.CASE_STUDIES, entityStatuses: caseStudies.map((cs) => cs.status) },
		{
			tab: KnowledgeDbTab.OBJECTION_HANDLING,
			entityStatuses: objections.map((o) => o.status),
		},
	];

	return entries
		.map(({ tab, entityStatuses }) => {
			const status = getTabOverallStatus(contents, tab, entityStatuses);

			return status ? { tab, status } : null;
		})
		.filter((ts): ts is TabStatus => ts !== null);
}
